import React from 'react';
import EChartsRenderer from '../../components/EChartsRenderer';
import type { ModelCalibrationBin } from '../../types/contactOptimization';

function pct(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined) return '\u2013';
  return `${(value * 100).toFixed(digits)}%`;
}

/**
 * Predicted vs. actual connect rate, one point per score decile -- the
 * standard way to read model calibration at a glance instead of doing
 * the "are these two columns close" comparison row-by-row in the
 * Calibration table below. Two overlaid lines (not a scatter + y=x
 * diagonal) so it reads left-to-right by decile, same as the table.
 */
export function CalibrationChart({ bins }: { bins: ModelCalibrationBin[] }) {
  const labels = bins.map((b) => `${pct(b.bin_low, 0)}\u2013${pct(b.bin_high, 0)}`);
  const option = React.useMemo(
    () => ({
      grid: { left: 48, right: 16, top: 36, bottom: 40 },
      xAxis: { type: 'category', data: labels, name: 'Score group', nameLocation: 'middle', nameGap: 28, axisLabel: { fontSize: 10 } },
      yAxis: { type: 'value', axisLabel: { formatter: (v: number) => `${v.toFixed(0)}%`, fontSize: 11 } },
      legend: { top: 0 },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any[]) => {
          const idx = params[0]?.dataIndex ?? 0;
          const bin = bins[idx];
          const marker = (seriesName: string) => params.find((p) => p.seriesName === seriesName)?.marker ?? '';
          return (
            `<strong>${labels[idx]}</strong><br/>` +
            `${marker('Predicted')}Predicted: ${pct(bin.predicted_mean, 2)}<br/>` +
            `${marker('Actual')}Actual: ${pct(bin.actual_rate, 2)}<br/>` +
            `${bin.count.toLocaleString()} leads`
          );
        },
      },
      series: [
        {
          name: 'Predicted',
          type: 'line',
          symbol: 'circle',
          symbolSize: 6,
          data: bins.map((b) => (b.predicted_mean != null ? +(b.predicted_mean * 100).toFixed(3) : null)),
        },
        {
          name: 'Actual',
          type: 'line',
          symbol: 'circle',
          symbolSize: 6,
          areaStyle: { opacity: 0.12 },
          data: bins.map((b) => (b.actual_rate != null ? +(b.actual_rate * 100).toFixed(3) : null)),
        },
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bins],
  );
  return <EChartsRenderer option={option} height={260} />;
}

/**
 * Top-feature gain, as a horizontal bar chart -- same data as the old
 * hand-rolled <Box> progress bars, just rendered with a real chart
 * (consistent with Timing's driver panel, which shows the same kind of
 * ranked-share data for the same model).
 */
export function FeatureImportanceBarChart({ topFeatures }: { topFeatures: [string, number][] }) {
  const sorted = [...topFeatures].reverse(); // input is desc; category axis wants ascending so the top feature ends up at the top
  const rawValues = sorted.map(([, value]) => value);
  const option = React.useMemo(
    () => ({
      grid: { left: 8, right: 56, top: 8, bottom: 8, containLabel: true },
      xAxis: { type: 'value', show: false },
      yAxis: { type: 'category', data: sorted.map(([name]) => name), axisLabel: { fontSize: 11 }, axisLine: { show: false } },
      tooltip: { trigger: 'item', formatter: (p: any) => `${p.name}: ${rawValues[p.dataIndex].toFixed(1)} influence score (relative to the top factor)` },
      series: [
        {
          type: 'bar',
          // Bar length is relative to the top feature's own gain -- not a
          // share of a total that sums to 100%, since raw LightGBM gain
          // scores don't normalize that way. The label shows the raw
          // gain value, not a fabricated percentage.
          data: rawValues.map((v) => +v.toFixed(2)),
          itemStyle: { borderRadius: [0, 3, 3, 0] },
          barMaxWidth: 16,
          label: { show: true, position: 'right', fontSize: 11, formatter: (p: any) => p.value.toFixed(1) },
        },
      ],
    }),
    [sorted, rawValues],
  );
  const height = Math.max(160, sorted.length * 30 + 16);
  return <EChartsRenderer option={option} height={height} />;
}
